import React, { useContext, useEffect, useMemo, useRef } from 'react';

import { Box, useMediaQuery } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQuery } from '@tanstack/react-query';
import { bool, string } from 'prop-types';
import { Form, Outlet, useNavigation, useParams, useSearchParams } from 'react-router-dom';

import ActionFormProvider, { ActionFormContext } from '../../../context/ActionForm';
import { AnalyticsContext } from '../../../context/Analytics';
import { CustomerRequestContext } from '../../../context/CustomerRequest';
import Header from './Header';
import TrustBanner from './TrustBanner';
import Loading from '../Loading';
import { VALID_STEPS, getQuote } from '../../../routes/step';
import getStripePublishableKey from '../../../utils/getStripePublishableKey';

const HIDE_TRUST_BANNER_STEPS = [VALID_STEPS.VIEW_QUOTE];

const ActionFormWrapper = ({ stripeLoaded, stripeGateway }) => {
  const { formRef } = useContext(ActionFormContext);
  const { customerInfo: customerSessionData } = useContext(CustomerRequestContext);
  const { userId } = useContext(AnalyticsContext);
  const { step } = useParams();

  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));
  const hideTrustBanner = HIDE_TRUST_BANNER_STEPS.includes(step);

  return (
    <Form method="post" ref={formRef}>
      {userId ? <input type="hidden" name="userId" value={userId} /> : null}
      {customerSessionData ? (
        <input type="hidden" name="customerSessionData" value={JSON.stringify(customerSessionData)} />
      ) : null}
      {stripeLoaded ? (
        // Lets checkout verify the card token's gateway matches the customer it gets attached to
        <input type="hidden" name="stripeGateway" value={stripeGateway ?? ''} />
      ) : null}
      <Box px={{ xs: 2, sm: 2 }} pb={{ xs: 2, sm: 0 }}>
        {step !== 'checkout' || stripeLoaded ? <Outlet /> : null}
      </Box>
      {!hideTrustBanner && isMobile ? (
        <Box pt={{ xs: 0 }} display="flex" width="100%">
          <TrustBanner />
        </Box>
      ) : null}
    </Form>
  );
};

ActionFormWrapper.propTypes = {
  stripeLoaded: bool,
  stripeGateway: string,
};

ActionFormWrapper.defaultProps = {
  stripeLoaded: false,
  stripeGateway: null,
};

const Layout = () => {
  const { step } = useParams();
  const [searchParams] = useSearchParams();
  const { state } = useNavigation();
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));

  const containerRef = useRef<HTMLElement>();

  const hideTrustBanner = HIDE_TRUST_BANNER_STEPS.includes(step);

  // Layout has no loader of its own - the step loader has already cached the quote by the time checkout renders
  const { data: quoteData } = useQuery(getQuote(searchParams.get('quoteId'), step));
  const isQuoteLoaded = !!quoteData;
  const stripeGateway = quoteData?.customer?.stripeGateway;
  const stripePublishableKey = getStripePublishableKey(stripeGateway);

  // Only create a new Stripe instance when the key changes, not on every quote refetch
  const stripePromise = useMemo(
    () => (step === 'checkout' && isQuoteLoaded ? loadStripe(stripePublishableKey) : null),
    [step, isQuoteLoaded, stripePublishableKey],
  );

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [step]);

  return (
    <Box
      minHeight={{ xs: 'calc(100vh - 51px)', md: 'calc(100vh - 105px)' }}
      display="flex"
      flexWrap="wrap"
      ref={containerRef}
    >
      <Header />
      {!isMobile ? (
        <Box
          pt={{ sm: 8, md: 11 }}
          display="flex"
          width="100%"
          sx={{
            '@media print': {
              display: 'none',
            },
          }}
        >
          {!hideTrustBanner ? <TrustBanner /> : null}
        </Box>
      ) : null}
      <Box
        width="100%"
        height="100%"
        pt={{ xs: 10, sm: 5 }}
        pb={{ xs: 20, sm: 15 }}
        bgcolor="background.default"
        display="flex"
        flexDirection="column"
        sx={{
          '@media print': {
            py: 0,
          },
        }}
      >
        {state === 'loading' ? (
          <Loading />
        ) : (
          <ActionFormProvider>
            {step === 'checkout' && stripePromise ? (
              // Elements does not support changing the `stripe` prop - remount if the key changes
              <Elements key={stripePublishableKey} stripe={stripePromise}>
                <ActionFormWrapper stripeLoaded stripeGateway={stripeGateway} />
              </Elements>
            ) : (
              <ActionFormWrapper />
            )}
          </ActionFormProvider>
        )}
      </Box>
    </Box>
  );
};

export default Layout;
